import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormConfig } from 'types/form/formConfig';
import { FormInitialData } from 'types/form/formInitialData';
import { ActorType } from '../enums/actorType';
import { ActorFormConfig } from '../types/actorFormConfig';
import { ActorFormValues } from './optim/useSubmitActorForm';
import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import { NaturalPersonFormFieldNames } from '../enums/naturalPersonFormFieldNames';
import { PersonalInformationFieldNames } from '../enums/personalInformationFieldNames';

// The only fields whose form default (FR) fills an empty value of an existing
// person: a country left blank almost always means France. Every other
// dropdown stays blank on purpose — an empty civility or role is a real
// "not filled in" the user must resolve consciously, not a default.
const FIELDS_DEFAULTED_WHEN_EMPTY: ReadonlySet<string> = new Set([
  AddressFormFieldNames.Country,
  PersonalInformationFieldNames.Nationality,
  PersonalInformationFieldNames.BirthCountry,
  NaturalPersonFormFieldNames.TaxResidenceCountryId,
]);

const isEmptyValue = (value: unknown) => value === undefined || value === null || value === '';

// Mapped actor values without the empty entries of the defaulted fields, so
// that the form default fills them instead of being overwritten by '' — a
// person created without those referentials (backfilled managers) otherwise
// opens with blank country dropdowns.
const withoutEmptyDefaultedValues = (values: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(values).filter(([key, value]) => !(FIELDS_DEFAULTED_WHEN_EMPTY.has(key) && isEmptyValue(value)))
  );

export const useActorForm = <T extends ActorFormValues>(formConfig: ActorFormConfig, initialData?: FormInitialData<ActorType, T>) => {
  const firstActorType = useMemo(() => Object.keys(formConfig)[0] as ActorType, [formConfig]);
  const [actorType, setActorType] = useState<ActorType>(firstActorType);

  const initialValues = useMemo(
    () =>
      Object.entries(formConfig).reduce(
        (acc, [key, value]) => {
          acc[key as ActorType] = value.initialValues;
          return acc;
        },
        {} as Partial<Record<ActorType, T>>
      ),
    [formConfig]
  );

  const computedInitialData = useMemo(() => {
    if (!initialData) return initialValues;

    return Object.keys(initialData).reduce(
      (acc, key) => {
        const actorKey = key as ActorType;
        if (actorKey in formConfig) {
          setActorType(actorKey)
          acc[actorKey] = {
            ...(formConfig as Record<ActorType, any>)[actorKey].initialValues,
            ...withoutEmptyDefaultedValues(initialData[actorKey] as Record<string, any>)
          };
        }
        return acc;
      },
      {} as Partial<Record<ActorType, T>>
    );
  }, [formConfig, initialData, initialValues]);

  const [savedValues, setSavedValues] = useState<Partial<{ [key in ActorType]: any }>>(initialData ? computedInitialData : initialValues);
  const [currentFormConfig, setCurrentFormConfig] = useState(
    formConfig[actorType as keyof ActorFormConfig] as FormConfig<any>
  );

  useEffect(() => {
    setCurrentFormConfig(formConfig[actorType as keyof ActorFormConfig]);
  }, [actorType, formConfig]);

  const handleActorTypeChange = useCallback(
    (
      newType: ActorType,
      currentValues: T,
      setCurrentValues: (values: T) => void
    ) => {
      setSavedValues(prev => ({ ...prev, [actorType]: currentValues }));
      setActorType(newType);
      setCurrentValues(savedValues[newType]);
    },
    [actorType, savedValues]
  );

  // Les valeurs initiales RÉELLEMENT injectées dans Formik (défauts du formulaire
  // fusionnés avec la personne mappée) : la référence correcte pour détecter une
  // modification — comparer aux valeurs mappées brutes crée des faux positifs
  // (null API vs "" défaut de champ).
  return { actorType, handleActorTypeChange, currentFormConfig, savedValues, mergedInitialValues: computedInitialData };
};
